import { useEffect, useRef, useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Send, Sparkles, X } from "lucide-react";
import { Badge, Button, Textarea } from "@/components/ui";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { useGenerateHtml, useRefineHtml } from "../hooks";
import { fragmentLabel, looksLikeLayoutChange, spliceFragment } from "../selectMode";

export interface Selection {
  path: number[];
  fragment: string;
}

interface Props {
  html: string;
  prompt: string;
  widthCm: number;
  heightCm: number;
  selection: Selection | null;
  onClearSelection: () => void;
  /** 整页重新生成后: 新 HTML + 合并了【追加要求】的新描述 */
  onWholePageUpdated: (html: string, mergedPrompt: string) => void;
  /** 片段改写后: 新整页 HTML + 新片段 (保持选中, 可继续追问) */
  onFragmentUpdated: (html: string, fragment: string) => void;
}

type Tone = "error" | "warn" | "ok";

interface Msg {
  role: "user" | "bot" | "system";
  text: string;
  tone?: Tone;
}

const TONE_ICON = { error: AlertCircle, warn: AlertTriangle, ok: CheckCircle2 } as const;
const TONE_CLASS = { error: "text-danger", warn: "text-warning", ok: "text-success" } as const;

function errText(e: unknown, what: string): string {
  if (e instanceof ApiError) return `${what}失败 (${e.status})`;
  if (e instanceof DOMException && e.name === "AbortError") return `${what}超时, 请稍后重试`;
  return "网络错误";
}

/** AI 修改助手: 未选中元素 → 整页重写; 选中元素 → refine-html 只改该片段 */
export function AiAssistPanel({
  html, prompt, widthCm, heightCm, selection,
  onClearSelection, onWholePageUpdated, onFragmentUpdated,
}: Props) {
  const [input, setInput] = useState("");
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const lastLayoutWarn = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const generate = useGenerateHtml();
  const refine = useRefineHtml();
  const busy = generate.isPending || refine.isPending;

  const add = (role: Msg["role"], text: string, tone?: Tone) => setMsgs((m) => [...m, { role, text, tone }]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [msgs]);

  // 选中新元素时聚焦输入框
  const selKey = selection ? selection.path.join(",") : "";
  useEffect(() => {
    if (selKey) inputRef.current?.focus();
  }, [selKey]);

  const label = selection ? fragmentLabel(selection.fragment) : "整页";

  const send = async () => {
    const instruction = input.trim();
    if (!instruction || busy) return;
    if (!html) {
      add("bot", "请先生成 HTML");
      return;
    }

    if (selection) {
      // 布局类指令: 提醒一次, 同样指令再发一次才执行
      if (looksLikeLayoutChange(instruction) && lastLayoutWarn.current !== instruction) {
        lastLayoutWarn.current = instruction;
        add("user", instruction);
        add(
          "bot",
          `这个指令涉及跨元素的布局, 只改选中的 ${label} 通常没用 — 这类改动请先「取消选中」, 让 AI 整页重写。\n仍要继续局部改写? 再发一次同样的指令就执行。`,
          "warn",
        );
        return;
      }
      add("user", instruction);
      setInput("");
      add("system", `正在改写选中元素 (${selection.fragment.length} 字符)...`);
      try {
        const resp = await refine.mutateAsync({
          fragment_html: selection.fragment,
          instruction,
          context_summary: prompt.slice(0, 200),
        });
        if (!resp.success || !resp.fragment_html) {
          add("bot", resp.error || "未知错误", "error");
          return;
        }
        const next = spliceFragment(html, selection.path, resp.fragment_html);
        if (!next) {
          add("bot", "无法定位选中元素 (源码可能已改动), 请重新选择", "error");
          onClearSelection();
          return;
        }
        onFragmentUpdated(next, resp.fragment_html);
        add(
          "bot",
          `已更新 (${selection.fragment.length} → ${resp.fragment_html.length} 字符)。如果预览没有视觉变化, 说明 AI 改的位置不影响显示, 建议改用整页模式重写。`,
          "ok",
        );
      } catch (e) {
        add("bot", errText(e, "改写"), "error");
      } finally {
        inputRef.current?.focus();
      }
      return;
    }

    // 整页: 原描述 + 【追加要求】 重新生成
    add("user", instruction);
    setInput("");
    add("system", "正在重新生成整页 HTML...");
    const merged = `${prompt.trim()}\n\n【追加要求】${instruction}`;
    try {
      const resp = await generate.mutateAsync({ text: merged, width_cm: widthCm, height_cm: heightCm });
      if (!resp.success || !resp.html) {
        add("bot", "生成失败: " + (resp.error || "未知错误"), "error");
        return;
      }
      onWholePageUpdated(resp.html, merged);
      add("bot", "已重新生成整页。", "ok");
    } catch (e) {
      add("bot", errText(e, "生成"), "error");
    } finally {
      inputRef.current?.focus();
    }
  };

  return (
    <section aria-label="AI 修改助手" className="flex min-h-0 flex-col rounded-xl border bg-card shadow-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b px-4 py-2.5">
        <span aria-hidden className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-white">
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <h3 className="text-sm font-semibold">AI 修改助手</h3>
        <Badge variant={selection ? "success" : "neutral"} className={cn("font-mono", !selection && "text-foreground")}>
          {label}
          {selection && (
            <button
              type="button"
              onClick={onClearSelection}
              title="取消选中, 切回整页"
              aria-label="取消选中"
              className="-mr-1 ml-0.5 rounded-full p-0.5 hover:bg-black/10"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </Badge>
        <span className="min-w-0 basis-full truncate text-caption text-muted-foreground sm:basis-auto sm:flex-1">
          {selection
            ? `AI 只改这个元素 (${selection.fragment.length} 字符)`
            : "未选中元素时, AI 会重写整张页面"}
        </span>
      </div>

      {msgs.length > 0 && (
        <div ref={listRef} className="max-h-48 space-y-2 overflow-y-auto bg-background/60 px-4 py-3">
          {msgs.map((m, i) => {
            const Icon = m.tone ? TONE_ICON[m.tone] : null;
            return (
              <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                <div
                  className={cn(
                    "flex max-w-[85%] gap-1.5 whitespace-pre-wrap break-words rounded-xl px-3 py-1.5 text-caption leading-5",
                    m.role === "user" && "rounded-br-sm bg-gradient-to-br from-brand to-primary text-primary-foreground",
                    m.role === "bot" && "rounded-tl-sm border bg-card text-foreground shadow-sm",
                    m.role === "system" && "px-0 italic text-muted-foreground",
                  )}
                >
                  {Icon && <Icon aria-hidden className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", TONE_CLASS[m.tone!])} />}
                  <span className="min-w-0">{m.text}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-end gap-2 border-t p-3">
        <Textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void send();
            }
          }}
          rows={2}
          aria-label="修改指令"
          placeholder="例: 价格改成黑色加粗 / 整体字再大一点 / 标题换成楷体... (Ctrl+Enter 发送)"
          className="flex-1 resize-none"
        />
        <Button onClick={() => void send()} disabled={!input.trim() || !html} loading={busy} icon={<Send />}>
          发送
        </Button>
      </div>
    </section>
  );
}
